/**
 * Parse date string in format "YYYY-MM-DD hh:mm:ss" or "YYYY-MM-DD"
 * @param dateString - Date string to parse
 * @returns Date object or null if invalid
 */
export const parseDateString = (dateString: string): Date | null => {
    // Try format "YYYY-MM-DD hh:mm:ss" first
    const dateTimeMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (dateTimeMatch) {
        const [, year, month, day, hour, minute, second] = dateTimeMatch;
        return new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hour),
            parseInt(minute),
            parseInt(second)
        );
    }
    
    // Try format "YYYY-MM-DD"
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
        const [, year, month, day] = dateMatch;
        return new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
        );
    }
    
    return null;
};

/**
 * Format a date as "YYYY-MM-DD hh:mm:ss"
 * @param date - Date to format
 * @returns Formatted date string
 */
export const formatDateForStorage = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

/**
 * Format a date for display
 * @param date - Date to format
 * @param inexactDate - If true, only show the year
 * @returns Formatted date string
 */
export const formatDate = (date: Date, inexactDate?: boolean): string => {
    if (inexactDate) {
        // For inexact dates, show only the year
        return date.getFullYear().toString();
    }
    return date.toLocaleDateString('sv-SE', { 
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Truncate text at first line break for aggregated view
 * @param text - Text to truncate
 * @returns Object with truncated text and truncated flag
 */
export const truncateAtLineBreak = (text: string): { text: string; truncated: boolean } => {
    const firstLineBreak = text.indexOf('\n');
    if (firstLineBreak === -1) {
        return { text, truncated: false };
    }
    return {
        text: text.substring(0, firstLineBreak).trim() + ' ...',
        truncated: true
    };
};

/**
 * Truncate text to one line of at most maxLength characters, appending an
 * ellipsis when content is dropped. Collapses multi-line text to its first line.
 * @param text - Text to truncate
 * @param maxLength - Maximum number of characters to keep
 * @returns Single-line, length-limited text
 */
export const truncateText = (text: string, maxLength: number): string => {
    const firstLine = text.split('\n')[0].trim();
    const hasMore = firstLine.length < text.trim().length;
    if (firstLine.length <= maxLength) {
        return hasMore ? `${firstLine} …` : firstLine;
    }
    return `${firstLine.substring(0, maxLength).trim()} …`;
};


